import type {
  CatalogItem,
  CatalogItemsResponse,
  CatalogItemCategory,
  CatalogFilterResponse,
  CatalogItemType,
  CatalogTypesResponse,
  WidgetQuoteRequest,
  QuoteResult,
  QuoteCrmSyncOutcome,
} from "@apex/shared";
import { crmSyncFailedMessage, isCrmSyncUnconfirmed } from "../lib/crmSyncCopy";

export interface WidgetApiConfig {
  apiUrl: string;
  apiKey: string;
}

/**
 * Response shape for the root items endpoint.
 * The widget fetches root items plus their filters in a single call. The
 * `rootHasPricing` flag is lifted from the parent CatalogItemType so the
 * Models grid can render price-aware item cards (#533) without a second
 * round-trip per render.
 */
export interface WidgetRootItemsResponse {
  items: CatalogItem[];
  filters: CatalogFilterResponse | null;
  categories: CatalogItemCategory[];
  rootHasPricing: boolean;
}

/**
 * Read-only subset of the tenant's `WidgetThemeConfig` served to the runtime
 * widget bundle via `GET /api/v1/widget/public-theme`. Mirrors the backend's
 * `PublicWidgetThemeDto`. Every field is nullable / optional because an
 * unconfigured tenant gets an all-null record; the widget then falls back to
 * its built-in client-side defaults.
 */
export interface PublicWidgetTheme {
  primaryColor: string | null;
  primaryHoverColor: string | null;
  primaryLightColor: string | null;
  backgroundColor: string | null;
  surfaceColor: string | null;
  borderColor: string | null;
  textColor: string | null;
  textMutedColor: string | null;
  fontFamily: string | null;
  fontSizeBase: string | null;
  borderRadius: string | null;
  borderRadiusLarge: string | null;
  logoUrl: string | null;
  useDarkMode: boolean;
  confirmationCopy: string[] | null;
  tenantWebsiteUrl: string | null;
  requireExplicitConsent: boolean;
  requireCompany: boolean;
}

/**
 * Default catalog type slugs matching the CatalogUnification migration seed data.
 * TODO: fetch dynamically from /api/v1/catalog/types instead of hardcoding.
 */
export const CATALOG_TYPE_SLUGS = {
  ROOT_TYPE_SLUG: "model",
  CHILD_TYPE_SLUG: "accessory",
} as const;

// Default per-request timeout. Customer-facing widget — failing fast on an
// unreachable API beats burning 12-15s of skeleton-loading state before the
// generic "Connection Error" fires (B16). Submit requests inherit the same
// budget; combined with the synchronous double-submit guard (B11), a client
// abort cannot fan out into duplicate quote rows.
const DEFAULT_TIMEOUT_MS = 8_000;

/**
 * Reads `crmSyncStatus` / `crmSyncError` off a quote-creation response.
 *
 * `crmSyncStatus` serialises as the enum's numeric value (0 Pending, 1 Forwarded, 2 Failed,
 * 3 Skipped); the name is accepted too so a future serializer change cannot silently turn a
 * failure back into a success. A body carrying an error and no status is read as a failure
 * for the same reason.
 *
 * `crmSyncError` is read for PRESENCE only, and on this endpoint it is a bare code
 * (`UNMAPPED_ITEMS`, `LINE_ITEMS_FAILED`, ...). The message behind that code is written for a
 * tenant admin - it names their catalogue codes, an Apex admin route and CRM record ids - so it
 * is deliberately not served here and must never be rendered to a customer. The sentence the
 * customer sees comes from `crmSyncCopy`.
 */
function readCrmSyncOutcome(
  response: Record<string, unknown> | undefined
): QuoteCrmSyncOutcome | undefined {
  const status = response?.crmSyncStatus;

  if (typeof status === "number") {
    const byValue: Record<number, QuoteCrmSyncOutcome> = {
      0: "pending",
      1: "forwarded",
      2: "failed",
      3: "skipped",
    };
    const outcome = byValue[status];
    if (outcome) return outcome;
  }

  if (typeof status === "string") {
    const name = status.toLowerCase();
    if (name === "pending" || name === "forwarded" || name === "failed" || name === "skipped") {
      return name;
    }
  }

  const error = response?.crmSyncError;
  if (typeof error === "string" && error.length > 0) {
    return "failed";
  }

  return undefined;
}

export class WidgetApiClient {
  private baseUrl: string;
  private apiKey: string;

  constructor(config: WidgetApiConfig) {
    // Remove trailing slash if present
    this.baseUrl = config.apiUrl.replace(/\/$/, "");
    this.apiKey = config.apiKey;
  }

  private async fetch<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    // Race a real abort against the caller's signal so external cancellation
    // (route changes, component unmount) still wins.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), DEFAULT_TIMEOUT_MS);
    const externalSignal = options.signal;
    if (externalSignal) {
      if (externalSignal.aborted) controller.abort();
      else externalSignal.addEventListener("abort", () => controller.abort(), { once: true });
    }

    let response: Response;
    try {
      response = await fetch(url, {
        ...options,
        signal: controller.signal,
        headers: {
          "Content-Type": "application/json",
          "Authorization": `ApiKey ${this.apiKey}`,
          ...options.headers,
        },
      });
    } catch (err) {
      // Surface an aborted-by-timeout fetch as a recognisable network error
      // so the ErrorView heuristic ("Failed to fetch" → CORS/unreachable
      // hint) catches it. The original DOMException name "AbortError" is
      // preserved on the cause for debugging.
      if ((err as DOMException | undefined)?.name === "AbortError") {
        throw new TypeError("Failed to fetch (request timed out)", { cause: err });
      }
      throw err;
    } finally {
      clearTimeout(timeoutId);
    }

    if (!response.ok) {
      const errorText = await response.text().catch(() => "Unknown error");

      // Parse the structured body produced by the API for 403/429 responses
      // (#554). Defensive — falls through to plain ApiError when missing.
      let structuredError: string | null = null;
      let requestId: string | null = null;
      if (errorText) {
        try {
          const body = JSON.parse(errorText) as { error?: string; requestId?: string };
          if (typeof body?.error === "string") structuredError = body.error;
          if (typeof body?.requestId === "string") requestId = body.requestId;
        } catch {
          // Body wasn't JSON; carry the raw text on .body as before.
        }
      }

      // Retry-After can be either seconds or an HTTP-date; we accept seconds.
      let retryAfterSeconds: number | null = null;
      const retryHeader = response.headers.get("Retry-After");
      if (retryHeader) {
        const asNum = Number(retryHeader);
        if (!Number.isNaN(asNum)) retryAfterSeconds = asNum;
      }

      throw new ApiError(
        `API request failed: ${response.status} ${response.statusText}`,
        response.status,
        errorText,
        retryAfterSeconds,
        structuredError,
        requestId
      );
    }

    // Handle empty responses
    const text = await response.text();
    if (!text) {
      return {} as T;
    }

    try {
      return JSON.parse(text) as T;
    } catch {
      throw new ApiError("Invalid JSON response", response.status, text);
    }
  }

  /**
   * Fetch all catalog types (with their categories and filters).
   * Used by the Models step to surface select + range filters in the
   * FilterBar, and by the Configure step to look up `hasPricing` on the
   * child type for price-aware item cards.
   */
  async getTypes(): Promise<CatalogTypesResponse> {
    return this.fetch<CatalogTypesResponse>("/api/v1/catalog/types");
  }

  /**
   * Resolve a single catalog type by slug. Returns null when the type does
   * not exist on the tenant.
   */
  async getTypeBySlug(slug: string): Promise<CatalogItemType | null> {
    const response = await this.getTypes();
    return response.types.find((t) => t.slug === slug) ?? null;
  }

  /**
   * Fetch all root items with filters.
   * Uses the catalog type endpoint for the root type slug. The filters
   * are resolved from the matching `CatalogItemType` since the items
   * endpoint does not return them.
   *
   * `search` is forwarded to the backend as `?search=` (#526) which performs
   * a case-insensitive contains match across Name + Code + Description.
   * `extraParams` carries through select-filter values keyed by metadata key.
   */
  async getRootItems(options?: {
    search?: string;
    extraParams?: Record<string, string>;
  }): Promise<WidgetRootItemsResponse> {
    const params = new URLSearchParams();

    if (options?.search && options.search.trim().length > 0) {
      params.append("search", options.search.trim());
    }

    if (options?.extraParams) {
      Object.entries(options.extraParams).forEach(([key, value]) => {
        if (value) {
          params.append(key, value);
        }
      });
    }

    const query = params.toString();
    const endpoint = `/api/v1/catalog/${CATALOG_TYPE_SLUGS.ROOT_TYPE_SLUG}/items${query ? `?${query}` : ""}`;

    // Fetch items + filters in parallel. The filters live on the type
    // definition (returned by /types) — we only need to fetch them once
    // per session, but for now we just pull them alongside the items;
    // upstream callers can cache as needed.
    const [response, rootType] = await Promise.all([
      this.fetch<CatalogItemsResponse>(endpoint),
      this.getTypeBySlug(CATALOG_TYPE_SLUGS.ROOT_TYPE_SLUG),
    ]);

    return {
      items: response.items,
      filters: rootType?.filters ?? null,
      categories: response.categories ?? [],
      // Default to true when the type lookup fails so we never silently
      // hide prices the tenant intends to expose. Tenants who opt out
      // (HasPricing=false) explicitly will return false here.
      rootHasPricing: rootType?.hasPricing ?? true,
    };
  }

  /**
   * Fetch a single item by ID
   */
  async getItem(id: string): Promise<CatalogItem> {
    return this.fetch<CatalogItem>(
      `/api/v1/catalog/items/${encodeURIComponent(id)}`
    );
  }

  /**
   * Fetch a single item by its human-readable code. Mirrors `getItem(id)`
   * but resolves against the by-code lookup endpoint — used to explicitly
   * resolve an `initial-item` attribute value that wasn't present in the
   * already-loaded root-items response (e.g. it's outside the default
   * page/filter window).
   */
  async getItemByCode(code: string): Promise<CatalogItem> {
    return this.fetch<CatalogItem>(
      `/api/v1/catalog/items/by-code/${encodeURIComponent(code)}`
    );
  }

  /**
   * Fetch child items compatible with a parent selection
   */
  async getCompatibleItems(
    typeSlug: string,
    parentItemId: string
  ): Promise<CatalogItemsResponse> {
    return this.fetch<CatalogItemsResponse>(
      `/api/v1/catalog/${encodeURIComponent(typeSlug)}/items?compatibleWith=${encodeURIComponent(parentItemId)}`
    );
  }

  /**
   * Submit a quote request.
   * Maps CatalogItem fields to the existing quote API contract.
   *
   * `notes` (#537) is forwarded as a free-form deal note. The honeypot,
   * firstInteractionAt and submittedAt fields (#538) drive server-side
   * anti-spam checks: when triggered the server returns 200 but does not
   * persist the quote.
   */
  async submitQuote(request: WidgetQuoteRequest): Promise<QuoteResult> {
    const body = {
      name: request.name,
      email: request.email,
      phoneNumber: request.phone,
      county: request.county,
      companyName: request.company,
      notes: request.notes,
      rootItemCode: request.rootItemCode,
      itemCodes: request.itemCodes ?? [],
      honeypot: request.honeypot,
      firstInteractionAt: request.firstInteractionAt ?? null,
      submittedAt: request.submittedAt ?? Date.now(),
    };

    const response = await this.fetch<Record<string, unknown>>(
      "/api/v1/quotes/new-customer",
      { method: "POST", body: JSON.stringify(body) }
    );

    // The quote row is persisted whatever happened downstream, so a 2xx is a success for the
    // customer's request. What it is NOT is proof the request reached the tenant's CRM: the
    // 201 body carries `crmSyncStatus` / `crmSyncError`, and hard-coding
    // "Quote submitted successfully" over them told customers their request had gone through
    // while the CRM write had failed (widget-verification D2/D4).
    const crmSync = readCrmSyncOutcome(response);
    const quoteId = typeof response?.id === "string" ? (response.id as string) : undefined;

    return {
      success: true,
      crmSync,
      // One definition of the failed-sync sentence, in src/lib/crmSyncCopy.ts. The
      // confirmation view renders what it finds here rather than writing its own.
      message: isCrmSyncUnconfirmed(crmSync)
        ? crmSyncFailedMessage(quoteId !== undefined)
        : "Quote submitted successfully",
      // Surfaced on the success view as "Quote {id}" (#539). The backend
      // returns the created quote's GUID as `id`; if the response is empty
      // (e.g., older mocks), we leave it undefined.
      quoteId,
    };
  }

  /**
   * Fetch the tenant's public widget theme (logo, success copy, "Visit our
   * website" CTA, requireCompany/requireExplicitConsent toggles).
   *
   * The widget calls this on mount so brand customisations saved in the
   * admin theme editor actually reach end customers. An unconfigured
   * tenant gets a 200 with an all-null record; the widget then falls back
   * to its built-in client-side defaults. Failures are best-effort —
   * callers should swallow and proceed with defaults rather than block
   * rendering on a theme-fetch error.
   */
  async getPublicTheme(): Promise<PublicWidgetTheme> {
    return this.fetch<PublicWidgetTheme>("/api/v1/widget/public-theme");
  }
}

/**
 * Custom API error with status code and response body
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly body: string,
    /** Parsed Retry-After value in seconds for 429 responses (#554). */
    public readonly retryAfterSeconds: number | null = null,
    /** Structured error body { error: "subscription_inactive" | ... } for 403/429 (#554). */
    public readonly structuredError: string | null = null,
    /** Optional support reference id from the response body. */
    public readonly requestId: string | null = null
  ) {
    super(message);
    this.name = "ApiError";
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  get isServerError(): boolean {
    return this.status >= 500;
  }

  get isRateLimited(): boolean {
    return this.status === 429;
  }
}

/**
 * Map an ApiError to a service-unavailable subtype + retry-after / message
 * (#554). Returns null when the error isn't a service-unavailable case.
 */
export function asServiceUnavailable(err: unknown): {
  subtype: "subscription" | "origin" | "key" | "key-rotated" | "rate-limit";
  message?: string;
  retryAfter?: number;
  requestId?: string;
} | null {
  if (!(err instanceof ApiError)) return null;
  if (err.status === 429) {
    return {
      subtype: "rate-limit",
      retryAfter: err.retryAfterSeconds ?? undefined,
      requestId: err.requestId ?? undefined,
    };
  }
  if (err.status !== 403) return null;
  switch (err.structuredError) {
    case "subscription_inactive":
      return { subtype: "subscription", requestId: err.requestId ?? undefined };
    case "origin_not_allowed":
      return { subtype: "origin", requestId: err.requestId ?? undefined };
    case "key_invalid":
    case "key_inactive":
      return { subtype: "key", requestId: err.requestId ?? undefined };
    case "key_rotated":
      return { subtype: "key-rotated", requestId: err.requestId ?? undefined };
    default:
      return null;
  }
}

/**
 * Create a new API client instance
 */
export function createApiClient(config: WidgetApiConfig): WidgetApiClient {
  return new WidgetApiClient(config);
}
